import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { openPdfInApp } from './openPdfInApp';

function addLogoImage(pdf, logoDataUrl, x, y, size) {
    if (!logoDataUrl) {
        return;
    }
    const format = logoDataUrl.includes('image/png') ? 'PNG' : 'JPEG';
    try {
        pdf.addImage(logoDataUrl, format, x, y, size, size);
    } catch (error) {
        // Skip logo when the image cannot be embedded.
    }
}

function drawPdfHeader(pdf, header) {
    const margin = 14;
    const pageWidth = pdf.internal.pageSize.getWidth();
    const headerTop = 8;
    const logoSize = 24;
    const lineCount = (header.addressLines || []).length + (header.gstNo ? 1 : 0) + 1;
    const headerHeight = Math.max(28, 10 + lineCount * 3.8 + 6);

    if (header.logoDataUrl) {
        addLogoImage(pdf, header.logoDataUrl, margin, headerTop + 2, logoSize);
    }

    const centerX = pageWidth / 2;
    let y = headerTop + 8;
    pdf.setFontSize(14);
    pdf.setTextColor(5, 77, 158);
    pdf.setFont('helvetica', 'bold');
    pdf.text(header.companyName || '', centerX, y, { align: 'center' });

    pdf.setTextColor(0, 0, 0);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    y += 5;
    (header.addressLines || []).forEach((line) => {
        pdf.text(line, centerX, y, { align: 'center', maxWidth: pageWidth - margin * 2 - logoSize - 8 });
        y += 3.8;
    });
    if (header.gstNo) {
        pdf.setFont('helvetica', 'bold');
        pdf.text(`GST No.: ${header.gstNo}`, centerX, y, { align: 'center' });
        y += 4.5;
    }

    if (header.documentTitle) {
        pdf.setFontSize(12);
        pdf.setFont('helvetica', 'bold');
        pdf.text(header.documentTitle, centerX, Math.min(y + 1, headerTop + headerHeight - 2), { align: 'center' });
    }

    let contentY = headerTop + headerHeight + 5;
    if (header.subtitle) {
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(header.subtitle, centerX, contentY, { align: 'center' });
        contentY += 5;
    }
    return contentY;
}

function buildPdfDocument(pageTitle, columns, records, summaryLines = [], header = null) {
    const pdfFormat = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
    let tableStartY = 18;

    if (header) {
        tableStartY = drawPdfHeader(pdfFormat, header);
    } else if (pageTitle) {
        pdfFormat.setFontSize(14);
        pdfFormat.text(pageTitle, 14, 12);
        tableStartY = 18;
    }

    if (summaryLines.length) {
        pdfFormat.setFontSize(10);
        summaryLines.forEach((line, index) => {
            pdfFormat.text(line, 14, tableStartY + index * 5);
        });
        tableStartY += summaryLines.length * 5 + 4;
    }

    const headFill = [110, 143, 236];
    const hasHeaderSubtotals = columns.some((col) => col.headerSubtotal);
    const dataKeys = columns.map((col) => col.dataKey || col.fieldName);
    const margin = 14;
    const pageWidth = pdfFormat.internal.pageSize.getWidth();
    const tableWidth = pageWidth - margin * 2;
    const columnStyles = {};
    dataKeys.forEach((key, index) => {
        const col = columns[index];
        const styles = {};
        if (col.halign === 'right' || col.align === 'right') {
            styles.halign = 'right';
        }
        if (col.dataBold || key === 'total_outstanding') {
            styles.fontStyle = 'bold';
        }
        if (col.cellWidth) {
            styles.cellWidth = col.cellWidth;
        }
        if (Object.keys(styles).length) {
            columnStyles[index] = styles;
        }
    });

    const autoTableOptions = {
        tableWidth,
        margin: { left: margin, right: margin },
        startY: tableStartY,
        styles: { fontSize: 8, valign: 'middle', overflow: 'linebreak' },
        headStyles: { fillColor: headFill, textColor: 255, valign: 'middle', fontSize: 8 },
        columnStyles,
    };

    if (hasHeaderSubtotals) {
        autoTableOptions.head = [
            columns.map((col) => ({
                content: col.headerSubtotal || '',
                styles: {
                    halign: col.halign || (col.headerSubtotal ? 'right' : 'left'),
                    fontStyle: col.headerSubtotal ? 'bold' : 'normal',
                    fillColor: headFill,
                    textColor: 255,
                },
            })),
            columns.map((col) => ({
                content: col.header || col.headerName || '',
                styles: {
                    halign: col.halign || 'left',
                    fillColor: headFill,
                    textColor: 255,
                },
            })),
        ];
        autoTableOptions.body = records.map((row) => dataKeys.map((key) => row[key] ?? ''));
    } else {
        autoTableOptions.columns = columns.map((col) => ({
            header: col.header || col.headerName,
            dataKey: col.dataKey || col.fieldName,
        }));
        autoTableOptions.body = records.map((row) => {
            const cleaned = {};
            dataKeys.forEach((key) => {
                const value = row?.[key];
                cleaned[key] = value === null || value === undefined || value === 'undefined' || value === 'null'
                    ? ''
                    : value;
            });
            return cleaned;
        });
    }

    pdfFormat.autoTable(autoTableOptions);
    return pdfFormat;
}

export function createPdfBlob(pageTitle, columns, records, summaryLines = [], header = null) {
    return buildPdfDocument(pageTitle, columns, records, summaryLines, header).output('blob');
}

export function createMultiSectionPdfBlob({ header = null, summaryLines = [], sections = [] }) {
    const pdfFormat = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'landscape' });
    const margin = 14;
    const headFill = [110, 143, 236];
    let startY = 18;

    if (header) {
        startY = drawPdfHeader(pdfFormat, header);
    }

    if (summaryLines.length) {
        pdfFormat.setFontSize(10);
        summaryLines.forEach((line, index) => {
            pdfFormat.text(line, margin, startY + index * 5);
        });
        startY += summaryLines.length * 5 + 4;
    }

    sections.forEach((section, index) => {
        if (index > 0) {
            pdfFormat.addPage();
            startY = margin;
        }

        pdfFormat.setFontSize(11);
        pdfFormat.setFont('helvetica', 'bold');
        pdfFormat.text(section.title || '', margin, startY);
        startY += 6;

        const columns = section.columns || [];
        const dataKeys = columns.map((col) => col.dataKey || col.fieldName);
        const columnStyles = {};
        dataKeys.forEach((key, colIndex) => {
            const col = columns[colIndex];
            const styles = {};
            if (col.halign === 'right' || col.align === 'right') {
                styles.halign = 'right';
            }
            if (col.cellWidth) {
                styles.cellWidth = col.cellWidth;
            }
            if (Object.keys(styles).length) {
                columnStyles[colIndex] = styles;
            }
        });

        const records = section.records || [];
        pdfFormat.autoTable({
            tableWidth: 'auto',
            margin: { left: margin, right: margin },
            startY,
            styles: { fontSize: 7.5, valign: 'middle', overflow: 'linebreak' },
            headStyles: { fillColor: headFill, textColor: 255, valign: 'middle', fontSize: 7.5 },
            columnStyles,
            columns: columns.map((col) => ({
                header: col.header || col.headerName,
                dataKey: col.dataKey || col.fieldName,
            })),
            body: records.map((row) => {
                const cleaned = {};
                dataKeys.forEach((key) => {
                    const value = row?.[key];
                    cleaned[key] = value === null || value === undefined ? '' : value;
                });
                return cleaned;
            }),
        });

        if (section.footerRow) {
            const footerY = pdfFormat.lastAutoTable.finalY + 2;
            pdfFormat.autoTable({
                margin: { left: margin, right: margin },
                startY: footerY,
                styles: { fontSize: 7.5, fontStyle: 'bold', fillColor: [238, 242, 255] },
                columnStyles,
                columns: columns.map((col) => ({
                    header: col.header || col.headerName,
                    dataKey: col.dataKey || col.fieldName,
                })),
                body: [section.footerRow],
            });
        }
    });

    return pdfFormat.output('blob');
}

export function exportAsPDF(pageTitle, columns, records, fileName, summaryLines = [], header = null) {
    const blob = buildPdfDocument(pageTitle, columns, records, summaryLines, header).output('blob');
    openPdfInApp(blob, {
        title: pageTitle || 'Report',
        downloadFileName: fileName,
    });
}
